import { useCallback, useMemo, useRef, useState } from "react";
import ReactFlow, { Background, Controls } from "reactflow";
import "reactflow/dist/style.css";
import * as XLSX from "xlsx";
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import {
  EXCEL_ACCEPT, isSupportedDatasetFile, loadDatasetFromFile, detectCompanyByHeaders,
  validateAgainstPattern, buildNormalizedRowsByCompany, analyzeCDR, resolveSelfLabel,
} from "./cdrEngine";
import { NODE_TYPES } from "./CDRNetworkGraph";
import SimpleTableWithFooter from "./SimpleTableWithFooter";
import { runSponsoredExport } from "./CdrExportAd";

export function buildCrossFileMatches(files) {
  const matches = [];

  // Shared IMEI: same IMEI in >=2 files' imeiSummary
  const imeiToFiles = new Map();
  files.forEach((f, i) => {
    (f.analysis.imeiSummary || []).forEach((entry) => {
      if (!imeiToFiles.has(entry.imei)) imeiToFiles.set(entry.imei, new Set());
      imeiToFiles.get(entry.imei).add(i);
    });
  });
  imeiToFiles.forEach((fileIndexSet, imei) => {
    if (fileIndexSet.size >= 2) matches.push({ type: "imei", value: imei, fileIndexes: [...fileIndexSet] });
  });

  // Shared contact (incl. direct contact): a bParty in one file's mfcAll
  // that is either (a) also in another file's mfcAll, or (b) equal to
  // another file's own selfLabel.
  const contactToFiles = new Map();
  files.forEach((f, i) => {
    (f.analysis.mfcAll || []).forEach((c) => {
      if (!contactToFiles.has(c.bParty)) contactToFiles.set(c.bParty, new Set());
      contactToFiles.get(c.bParty).add(i);
    });
  });
  files.forEach((f, i) => {
    if (contactToFiles.has(f.selfLabel)) contactToFiles.get(f.selfLabel).add(i);
  });
  contactToFiles.forEach((fileIndexSet, contact) => {
    if (fileIndexSet.size < 2) return;
    const isDirect = files.some((f) => f.selfLabel === contact);
    matches.push({ type: isDirect ? "direct-contact" : "contact", value: contact, fileIndexes: [...fileIndexSet] });
  });

  // Shared location: same location string in >=2 files' mflAll
  const locationToFiles = new Map();
  files.forEach((f, i) => {
    (f.analysis.mflAll || []).forEach((entry) => {
      if (entry.location === "(blank)") return;
      if (!locationToFiles.has(entry.location)) locationToFiles.set(entry.location, new Set());
      locationToFiles.get(entry.location).add(i);
    });
  });
  locationToFiles.forEach((fileIndexSet, location) => {
    if (fileIndexSet.size >= 2) matches.push({ type: "location", value: location, fileIndexes: [...fileIndexSet] });
  });

  return matches;
}

export function computeCrossFileLayout(fileCount) {
  const radius = 180;
  return Array.from({ length: fileCount }, (_, i) => {
    const angle = (2 * Math.PI * i) / fileCount - Math.PI / 2;
    return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) };
  });
}

const EDGE_STYLE_BY_TYPE = {
  imei: { stroke: "#dc2626", strokeWidth: 3 },
  "direct-contact": { stroke: "#2563eb", strokeWidth: 3 },
  contact: { stroke: "#2563eb", strokeWidth: 1.5 },
  location: { stroke: "#9ca3af", strokeWidth: 1.5, strokeDasharray: "4 3" },
};

export function buildCrossFileGraphData(validFiles, matches) {
  const positions = computeCrossFileLayout(validFiles.length);
  const nodes = validFiles.map((f, i) => ({
    id: `file-${i}`,
    type: "cdrNode",
    position: positions[i],
    data: {
      kind: "hub",
      label: f.selfLabel,
      subLabel: f.fileName,
      tooltip: `${f.fileName} — Subscriber: ${f.selfLabel}`,
    },
    draggable: false,
  }));

  const edges = [];
  matches.forEach((m, matchIndex) => {
    const style = EDGE_STYLE_BY_TYPE[m.type];
    for (let a = 0; a < m.fileIndexes.length; a += 1) {
      for (let b = a + 1; b < m.fileIndexes.length; b += 1) {
        edges.push({
          id: `edge-${matchIndex}-${a}-${b}`,
          source: `file-${m.fileIndexes[a]}`,
          target: `file-${m.fileIndexes[b]}`,
          label: m.value,
          style: { ...style },
        });
      }
    }
  });

  return { nodes, edges };
}

const MAX_FILES = 5;

function describeValidationError(validation) {
  const parts = [];
  if (validation.missing.length) parts.push(`Missing: ${validation.missing.join(", ")}`);
  if (validation.extra.length) parts.push(`Extra: ${validation.extra.join(", ")}`);
  return parts.length ? parts.join(" · ") : "Column format mismatch.";
}

async function parseAndValidateFile(file) {
  const base = { fileName: file.name, status: "error", errorMessage: "", company: "", selfLabel: "", analysis: null, normRows: [] };
  if (!isSupportedDatasetFile(file)) {
    return { ...base, errorMessage: "Unsupported file type." };
  }
  try {
    const dataset = await loadDatasetFromFile(file);
    const headers = Array.isArray(dataset?.headers) ? dataset.headers : [];
    const rows = Array.isArray(dataset?.rows) ? dataset.rows : [];
    const hdrDetect = detectCompanyByHeaders(headers);
    const chosen = hdrDetect.detected === "Unknown" ? "Unknown" : hdrDetect.detected;
    const validation = chosen !== "Unknown"
      ? validateAgainstPattern(headers, chosen)
      : { ok: false, company: "Unknown", missing: [], extra: headers };

    if (!validation.ok) {
      return { ...base, company: validation.company, errorMessage: describeValidationError(validation) };
    }

    const normRows = buildNormalizedRowsByCompany(rows, validation.company);
    const analysis = analyzeCDR(normRows);
    const selfLabel = resolveSelfLabel(normRows);
    return { ...base, status: "ok", company: validation.company, selfLabel, analysis, normRows };
  } catch (err) {
    return { ...base, errorMessage: err instanceof Error ? err.message : "Failed to read file." };
  }
}

export default function CDRMultiAnalysis() {
  const inputRef = useRef(null);
  const nextIdRef = useRef(0);
  const [files, setFiles] = useState([]);
  const [crossRef, setCrossRef] = useState(null); // { matches, validFiles } | null

  const validCount = files.filter((f) => f.status === "ok").length;
  const canRun = validCount >= 2;

  const addFile = async (file) => {
    if (files.length >= MAX_FILES) return;
    const id = nextIdRef.current;
    nextIdRef.current += 1;
    setFiles((prev) => [...prev, { id, fileName: file.name, status: "parsing", errorMessage: "", company: "", selfLabel: "", analysis: null, normRows: [] }]);
    setCrossRef(null);
    const result = await parseAndValidateFile(file);
    setFiles((prev) => prev.map((f) => (f.id === id ? { id, ...result } : f)));
  };

  const handleFileInputChange = async (e) => {
    const selectedFiles = Array.from(e.target.files || []).slice(0, Math.max(0, MAX_FILES - files.length));
    e.target.value = "";
    // Parse large workbooks one at a time so multiple selections do not
    // compete for memory and lock up the browser UI.
    for (const file of selectedFiles) {
      await addFile(file);
    }
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
    setCrossRef(null);
  };

  const runCrossReference = useCallback(() => {
    const validFiles = files.filter((f) => f.status === "ok");
    const matches = buildCrossFileMatches(
      validFiles.map((f) => ({ fileLabel: f.fileName, selfLabel: f.selfLabel, analysis: f.analysis }))
    );
    setCrossRef({ matches, validFiles });
  }, [files]);

  const graphData = useMemo(() => {
    if (!crossRef) return { nodes: [], edges: [] };
    const validFilesForGraph = crossRef.validFiles.map((f) => ({ fileName: f.fileName, selfLabel: f.selfLabel }));
    return buildCrossFileGraphData(validFilesForGraph, crossRef.matches);
  }, [crossRef]);

  const tableRows = useMemo(() => {
    if (!crossRef) return [];
    return crossRef.matches.map((m, i) => ({
      __id: i,
      type: m.type,
      value: m.value,
      filesInvolved: m.fileIndexes.map((idx) => crossRef.validFiles[idx].fileName).join(", "),
    }));
  }, [crossRef]);

  const exportToExcel = useCallback(() => {
    if (!crossRef) return;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([
        ["Type", "Value", "Files Involved"],
        ...crossRef.matches.map((m) => [m.type, m.value, m.fileIndexes.map((idx) => crossRef.validFiles[idx].fileName).join(", ")]),
      ]),
      "Cross_File_Matches"
    );
    XLSX.writeFile(wb, `CDR_Cross_File_${Date.now()}.xlsx`);
  }, [crossRef]);

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={EXCEL_ACCEPT}
        style={{ display: "none" }}
        onChange={handleFileInputChange}
      />
      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        Add two to five supported CDR files. Files are processed locally in your browser and compared for shared contacts, IMEIs, and locations.
      </Alert>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
        <Button
          size="medium"
          variant="contained"
          disabled={files.length >= MAX_FILES}
          onClick={() => inputRef.current?.click()}
        >
          Add CDR files
        </Button>
        <Chip size="small" variant="outlined" label={`${files.length} of ${MAX_FILES} files`} />
        {validCount > 0 && <Chip size="small" color="success" variant="outlined" label={`${validCount} validated`} />}
      </Box>

      <Stack spacing={1}>
        {files.map((f) => (
        <Paper
          key={f.id}
          variant="outlined"
          sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", px: 1.5, py: 1.25, borderRadius: 2 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, minWidth: { sm: 210 } }}>{f.fileName}</Typography>
          {f.status === "parsing" && <CircularProgress size={14} />}
          {f.status === "ok" && (
            <>
              <Chip size="small" label={`✅ ${f.company}`} />
              <Typography variant="caption" color="text.secondary">Subscriber: {f.selfLabel}</Typography>
            </>
          )}
          {f.status === "error" && (
            <>
              <Chip size="small" color="error" label="❌ Invalid" />
              <Typography variant="caption" color="error">{f.errorMessage}</Typography>
            </>
          )}
          <Button size="small" color="error" sx={{ ml: { sm: "auto" } }} onClick={() => removeFile(f.id)}>Remove</Button>
        </Paper>
      ))}
      </Stack>

      {files.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button size="medium" variant="contained" disabled={!canRun} onClick={runCrossReference}>
            Run Cross-Reference
          </Button>
          {!canRun && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Add at least 2 valid files to cross-reference.
            </Typography>
          )}
        </Box>
      )}

      {crossRef && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: 900, mb: 1 }}>
            {crossRef.matches.length} match{crossRef.matches.length === 1 ? "" : "es"} found across {crossRef.validFiles.length} files
          </Typography>
          <Box sx={{ width: "100%", height: 420, border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden", bgcolor: "background.default" }}>
            <ReactFlow
              nodes={graphData.nodes}
              edges={graphData.edges}
              nodeTypes={NODE_TYPES}
              nodesDraggable={false}
              nodesConnectable={false}
              onInit={(instance) => instance.fitView({ padding: 0.2 })}
              fitView
              style={{ width: "100%", height: "100%", minHeight: 420 }}
            >
              <Background />
              <Controls />
            </ReactFlow>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Button size="small" variant="outlined" onClick={() => runSponsoredExport(exportToExcel)} sx={{ mb: 1 }}>
              Export Excel
            </Button>
            <SimpleTableWithFooter
              columns={[
                { key: "type", label: "Type" },
                { key: "value", label: "Value" },
                { key: "filesInvolved", label: "Files Involved" },
              ]}
              rows={tableRows}
              emptyText="No cross-file matches found."
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
